/**
 * useLiveProp — drží prop komponenty **živě napojený** na React Query cache.
 *
 * Proč to existuje:
 * ─────────────────
 * Detail / Edit / Settings modal dostane entitu (např. `inst: SupabaseInstance`)
 * jako prop v okamžiku otevření. Uvnitř modalu se pak volají mutations
 * (toggle, save), které invalidují parent query (`invalidateQueries([...])`).
 *
 * Parent refetchne a má čerstvá data, **ale modal dál vidí starý snapshot
 * z propu**. Toggle switch po kliknutí neudělá nic (ve skutečnosti se stav
 * v DB změnil, ale UI pořád ukazuje starý stav, protože `inst.enabled`
 * je starý prop).
 *
 * Tento hook čte z query cache SAMOSTATNĚ (enabled: false — nefetchuje),
 * ale subscribuje se na změny. Když parent query dostane nová data,
 * hook vrátí novou entitu a modal se překreslí.
 *
 * Použití:
 * ────────
 * ```tsx
 * function EditModal({ inst: initialInst, onClose }: Props) {
 *   const inst = useLiveProp(initialInst, {
 *     queryKey: ['supabase-instances'],
 *     select: (data) => data.instances?.find((i) => i.name === initialInst.name),
 *   });
 *   // inst je vždy fresh — po invalidateQueries se překreslí
 *   return <Toggle value={inst.enabled} onSave={...} />
 * }
 * ```
 *
 * Kdy NE:
 * ───────
 * - Komponenty co dostávají data jako plain objekt jednorázově (formulář
 *   pro create, read-only detail bez akcí)
 * - Případy kdy má být snapshot intencionálně zmrazený (porovnání starého
 *   vs nového stavu)
 */
import { useQuery } from '@tanstack/react-query';

export interface UseLivePropOptions<TCache, TEntity> {
  /** Query key stejný jako v parent komponentě — cache se sdílí */
  queryKey: readonly unknown[];
  /** Extrakce entity z cache (např. najít v poli instancí podle ID) */
  select: (cache: TCache) => TEntity | null | undefined;
}

/**
 * Vrátí vždy nejnovější variantu entity z query cache, nebo fallback pokud
 * cache ještě nemá data (nebo selector nevrátí nic).
 */
export function useLiveProp<TCache, TEntity>(
  fallback: TEntity,
  opts: UseLivePropOptions<TCache, TEntity>,
): TEntity {
  // enabled: false → hook nefetchuje sám, jen čte z cache a subscribuje se
  // na její změny. Parent (nebo kdokoli jiný) musí být odpovědný za fetch.
  const { data } = useQuery<TCache>({
    queryKey: opts.queryKey,
    enabled: false,
  });

  if (!data) return fallback;
  const fresh = opts.select(data);
  return fresh ?? fallback;
}
